/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useContext, useRef, useState } from "react";
import { Button, LinearProgress, List, ListSubheader } from "@mui/material";
import { Backspace, Save } from "@mui/icons-material";
import Layer from "./Layer";
import { AuthenticatedContext } from "../Authenticated/Authenticated";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getActiveLayers, saveLayers } from "../../api";
import { AxiosError } from "axios";
import { RosterLayer } from "RosterData";

interface LayersListProps {
  width: number;
  showHeader: boolean;
  onClose: any;
}

const LayersList = (props: LayersListProps) => {
  const { handleError, initialLayers, setInitialLayers, getLatestReportData } =
    useContext(AuthenticatedContext);
  const [layerState, setLayerState] = useState<string[]>(initialLayers);
  const valueRef = useRef<any>(null);
  const { data, isLoading } = useQuery({
    queryFn: getActiveLayers,
    queryKey: ["layers"],
    onError: (error: AxiosError) => {
      handleError(error);
    },
  });

  const saveLayersToPreferences = useMutation({
    mutationFn: (layers: string[]) => {
      return saveLayers(layers);
    },
    onSuccess: () => {
      setInitialLayers(layerState);
      getLatestReportData();
    },
    onError: (error: AxiosError) => handleError(error),
  });

  const handleLayerStateChange = (layerId: string) => {
    setLayerState((prevState) => {
      if (prevState.includes(layerId)) {
        return prevState.filter((str) => str !== layerId);
      } else {
        return [...prevState, layerId];
      }
    });
  };

  const handleSave = () => {
    saveLayersToPreferences.mutate(layerState);
    props.onClose();
  };
  const handleClear = () => {
    setLayerState([]);
    if (valueRef.current) {
      clearTimeout(valueRef.current);
    }
    valueRef.current = setTimeout(() => {
      saveLayersToPreferences.mutate([]);
    }, 500);
  };

  return (
    <>
      <List
        sx={{ width: props.width }}
        dense
        component="nav"
        subheader={
          props.showHeader && (
            <ListSubheader component="div">Layers</ListSubheader>
          )
        }
      >
        {isLoading ? (
          <LinearProgress />
        ) : (
          data?.map((layer: RosterLayer) => (
            <Layer
              id={layer.id}
              key={layer.id}
              layerState={layerState}
              setLayerState={handleLayerStateChange}
              disabled={false}
              name={layer.layerDisplayName}
              description={layer.description}
              iconName={layer.icon}
            />
          ))
        )}
        <Layer
          id="BASE"
          description="Core Roster Data"
          disabled
          iconName="BASE"
          name="Base"
        />
      </List>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={handleSave}
          sx={{ mr: 1 }}
          variant="contained"
          size="small"
          startIcon={<Save />}
        >
          Save
        </Button>
        <Button
          onClick={handleClear}
          variant="outlined"
          size="small"
          endIcon={<Backspace />}
        >
          Clear
        </Button>
      </div>
    </>
  );
};

export default LayersList;
